import React, { useState, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import PrintNREMTCustomModuleBuilder from '../components/PrintNREMTCustomModuleBuilder_styled';

const NREMTPrintBuilderNew = () => {
  
  // Add print styles for better pagination
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        .break-after-page {
          page-break-after: always;
        }
        .page-break-inside-avoid {
          page-break-inside: avoid;
        }
        .break-inside-avoid {
          break-inside: avoid;
        }
        @page {
          margin: 0.5in;
          size: letter;
        }
        body {
          font-size: 10px;
          line-height: 1.1;
        }
        table {
          font-size: 9px;
        }
        td, th {
          padding: 1px !important;
          height: 20px !important;
          vertical-align: middle !important;
        }
        /* Ensure text alignment is preserved */
        .text-center {
          text-align: center !important;
        }
        .text-left {
          text-align: left !important;
        }
        .no-print {
          display: none !important;
        }
        .print-only {
          display: block !important;
        }
        .hide-on-print {
          display: none !important;
        }
        * {
          margin: 0 !important;
        }
      }
      
      /* Hide number input arrows */
      .no-arrows::-webkit-outer-spin-button,
      .no-arrows::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      
      .no-arrows[type=number] {
        -moz-appearance: textfield;
      }
      
      /* Show actions sidebar when hovering over table container */
      .table-container:hover .actions-sidebar {
        opacity: 1 !important;
      }
      
      /* Drag and drop visual feedback */
      .dragging-button {
        opacity: 0.7;
        transform: rotate(2deg);
      }
      
      .drop-indicator {
        background: linear-gradient(90deg, transparent, #60a5fa, transparent);
        height: 3px;
        border-radius: 2px;
        margin: 2px 0;
        animation: pulse 1.5s ease-in-out infinite;
      }
      
      @keyframes pulse {
        0%, 100% { opacity: 0.6; }
        50% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Simple state for the assessment form - initialize with empty state
  const [assessmentData, setAssessmentData] = useState({
    title: 'NREMT Practical Skill Assessment',
    candidateName: '',
    examinerName: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    rows: []
  });

  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showCustomModuleBuilder, setShowCustomModuleBuilder] = useState(false);
  const [editingCustomModule, setEditingCustomModule] = useState(null); // Track which custom module is being edited
  const [editingField, setEditingField] = useState(null); // Track which field is being edited
  const [tempValue, setTempValue] = useState(''); // Temporary value while editing
  const [savedForms, setSavedForms] = useState([]); // List of saved forms
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [saveFormName, setSaveFormName] = useState(''); // Name for saving form
  const [currentFormId, setCurrentFormId] = useState(null); // Track currently loaded form
  const [currentFormName, setCurrentFormName] = useState(''); // Track current form name

  // Load saved forms from localStorage on component mount
  React.useEffect(() => {
    const saved = localStorage.getItem('nremt-saved-forms');
    if (saved) {
      try {
        setSavedForms(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading saved forms:', error);
      }
    }
    
    // Load the last active form if it exists
    const lastFormData = localStorage.getItem('nremt-current-form');
    if (lastFormData) {
      try {
        const { formId, formName, data } = JSON.parse(lastFormData);
        setAssessmentData(data);
        setCurrentFormId(formId);
        setCurrentFormName(formName);
      } catch (error) {
        console.error('Error loading current form:', error);
      }
    }
  }, []);

  // Initialize criticalRows for custom modules that don't have it
  React.useEffect(() => {
    setAssessmentData(prev => ({
      ...prev,
      rows: prev.rows.map(row => {
        if (row.type === 'custom' && row.moduleInfo && !row.criticalRows) {
          return {
            ...row,
            criticalRows: Array(row.moduleInfo.gridRows).fill(false)
          };
        }
        return row;
      })
    }));
  }, []); // Run once on mount

  // Start editing a field
  const startEditing = useCallback((fieldId, currentValue) => {
    setEditingField(fieldId);
    setTempValue(currentValue);
  }, []);

  // Update a row
  const updateRow = useCallback((id, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      rows: prev.rows.map(row => 
        row.id === id ? { ...row, [field]: value } : row
      )
    }));
  }, []);

  // Update individual critical row in custom module
  const updateCustomRowCritical = useCallback((rowId, criticalRowIndex, value) => {
    setAssessmentData(prev => ({
      ...prev,
      rows: prev.rows.map(row => {
        if (row.id === rowId && row.type === 'custom') {
          // Ensure criticalRows exists
          const currentCriticalRows = row.criticalRows || Array(row.moduleInfo?.gridRows || 0).fill(false);
          const newCriticalRows = [...currentCriticalRows];
          newCriticalRows[criticalRowIndex] = value;
          return { ...row, criticalRows: newCriticalRows };
        }
        return row;
      })
    }));
  }, []);

  // Save current form data to localStorage whenever it changes
  React.useEffect(() => {
    // Only save if there's actual data (not just the initial empty state)
    if (assessmentData.rows.length > 0 || assessmentData.candidateName || assessmentData.examinerName || currentFormId) {
      const currentFormData = {
        formId: currentFormId,
        formName: currentFormName,
        data: assessmentData
      };
      localStorage.setItem('nremt-current-form', JSON.stringify(currentFormData));
    }
  }, [assessmentData, currentFormId, currentFormName]);

  // Confirm edit
  const confirmEdit = useCallback((rowId, field) => {
    updateRow(rowId, field, tempValue);
    setEditingField(null);
    setTempValue('');
  }, [tempValue, updateRow]);

  // Cancel edit
  const cancelEdit = useCallback(() => {
    setEditingField(null);
    setTempValue('');
  }, []);

  // Generate PDF using browser print functionality with dedicated print window
  const generatePDF = () => {
    // Create the HTML content for printing
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>NREMT Assessment Form</title>
        <style>
          @page {
            margin: 0.5in;
            size: letter;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 10px;
            line-height: 1.1;
            margin: 0;
            padding: 0;
          }
          .assessment-form {
            max-width: 100%;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 15px;
          }
          .header h1 {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 15px;
          }
          .header-info {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr 1fr;
            gap: 10px;
            margin-bottom: 15px;
            font-size: 10px;
          }
          .header-field {
            display: flex;
            align-items: center;
          }
          .header-field label {
            font-weight: bold;
            margin-right: 5px;
          }
          .header-field .underline {
            border-bottom: 1px solid #000;
            min-width: 80px;
            height: 16px;
            margin-left: 5px;
            display: inline-block;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th, td {
            border: 1px solid #000;
            padding: 1px;
            vertical-align: middle;
            text-align: left;
            height: 1%; /* Allow height to be flexible */
          }
          th {
            background-color: #f0f0f0;
            font-weight: bold;
            text-align: center;
          }
          .text-center {
            text-align: center !important;
          }
          .section-col { width: 16.6%; }
          .task-col { width: calc(100% - 180px); }
          .critical-col { width: 60px; text-align: center; }
          .points-col { width: 50px; text-align: center; }
          .score-col { width: 50px; text-align: center; }
          /* Heading row styles - remove internal borders */
          .heading-row td {
            border-left: none !important;
            border-right: none !important;
          }
          .heading-row td:first-child {
            border-left: 1px solid #000 !important;
          }
          .heading-row td:last-child {
            border-right: 1px solid #000 !important;
          }
          /* Sub section row styles - remove internal borders */
          .sub-section-row td {
            border-left: none !important;
            border-right: none !important;
          }
          .sub-section-row td:first-child {
            border-left: 1px solid #000 !important;
          }
          .sub-section-row td:last-child {
            border-right: 1px solid #000 !important;
          }
          .signature-section {
            margin-top: 30px;
          }
          .signature-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }
          .signature-field {
            font-size: 10px;
          }
          .signature-field label {
            font-weight: bold;
            margin-bottom: 5px;
            display: block;
          }
          .signature-line {
            border-bottom: 1px solid #000;
            height: 20px;
          }
          .comments-section {
            margin-bottom: 20px;
          }
          .comments-box {
            border: 1px solid #000;
            height: 60px;
            margin-top: 5px;
          }
          .performance-section {
            text-align: center;
            font-size: 10px;
          }
          .performance-options {
            display: inline-flex;
            align-items: center;
            gap: 20px;
          }
          .checkbox {
            width: 12px;
            height: 12px;
            border: 1px solid #000;
            display: inline-block;
            margin: 0 5px;
          }
          .custom-module-grid {
            display: grid;
            gap: 0;
            border: none;
            width: 100%;
          }
          .custom-module-cell {
            border-right: 1px solid #9CA3AF;
            border-bottom: 1px solid #9CA3AF;
            padding: 2px;
            text-align: center;
            min-height: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .custom-module-cell:last-child {
            border-right: none;
          }
          .break-after-page {
            page-break-after: always;
          }
        </style>
      </head>
      <body>
        <div class="assessment-form">
          <div class="header">
            <h1>${assessmentData.title}</h1>
            <div class="header-info">
              <div class="header-field">
                <label>Candidate Name:</label>
                <span class="underline">${assessmentData.candidateName || ''}</span>
              </div>
              <div class="header-field">
                <label>Examiner Name:</label>
                <span class="underline">${assessmentData.examinerName || ''}</span>
              </div>
              <div class="header-field">
                <label>Date:</label>
                <span class="underline">${assessmentData.date || ''}</span>
              </div>
              <div class="header-field">
                <label>Start:</label>
                <span class="underline">${assessmentData.startTime || ''}</span>
                <label style="margin-left: 10px;">End:</label>
                <span class="underline">${assessmentData.endTime || ''}</span>
              </div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th class="section-col">Section</th>
                <th class="task-col">Task/Criteria</th>
                <th class="critical-col">Critical</th>
                <th class="points-col">Points</th>
                <th class="score-col">Score</th>
              </tr>
            </thead>
            <tbody>
              ${assessmentData.rows.map((row, index) => {
                let taskContent = row.task || '';
                let pointsValue = '';
                
                // Calculate section total for section-total rows
                if (row.type === 'section-total') {
                  let total = 0;
                  let startIndex = 0;
                  
                  // Find the previous section total to determine where this section starts
                  for (let i = index - 1; i >= 0; i--) {
                    if (assessmentData.rows[i].type === 'section-total') {
                      startIndex = i + 1;
                      break;
                    }
                  }
                  
                  // Sum all points from startIndex to index (exclusive)
                  for (let i = startIndex; i < index; i++) {
                    const currentRow = assessmentData.rows[i];
                    if (currentRow.type === 'regular' || currentRow.type === 'custom') {
                      if (currentRow.type === 'custom' && currentRow.moduleInfo) {
                        total += currentRow.moduleInfo.totalPoints || currentRow.points || 0;
                      } else {
                        total += currentRow.points || 0;
                      }
                    }
                  }
                  
                  pointsValue = total.toString();
                } else if (row.type === 'custom' && row.moduleInfo) {
                  pointsValue = (row.moduleInfo.totalPoints || row.points).toString();
                } else {
                  pointsValue = (row.points || '').toString();
                }
                
                // Handle custom modules
                if (row.type === 'custom' && row.moduleInfo) {
                  const gridCells = row.moduleInfo.gridCells.map(cell => {
                    let content = '';
                    const cellTypes = cell.types || [cell.type || 'blank'];
                    const hasText = cellTypes.includes('text');
                    const hasCheckbox = cellTypes.includes('checkbox');
                    
                    if (hasText && hasCheckbox) {
                      content = `<div style="display: flex; align-items: center; justify-content: center; width: 100%; font-size: 12px;"><span style="margin-right: 2px;">${cell.content || ''}</span><span>☐</span></div>`; 
                    } else if (hasText) {
                      content = `<div style="display: flex; align-items: center; justify-content: center; width: 100%; font-size: 12px;"><span>${cell.content || ''}</span></div>`;
                    } else if (hasCheckbox) {
                      content = `<div style="display: flex; align-items: center; justify-content: center; width: 100%; font-size: 12px;"><span>☐</span></div>`;
                    } else {
                      content = '';
                    }
                    
                    return `<div class="custom-module-cell">${content}</div>`;
                  }).join('');
                  
                  taskContent = `<div class="custom-module-grid" style="grid-template-columns: repeat(${row.moduleInfo.gridColumns}, 1fr); grid-template-rows: repeat(${row.moduleInfo.gridRows}, 1fr); min-height: ${row.moduleInfo.gridRows * 16}px;">${gridCells}</div>`;
                }
                
                // Handle heading rows - center align and make bold/larger
                if (row.type === 'heading') {
                  taskContent = `<div style="text-align: center; font-weight: bold; font-size: 11px;">${row.task || ''}</div>`;
                }
                
                // Handle sub-section rows - center align section text, make bold/larger
                let sectionContent = row.section || '';
                if (row.type === 'sub-section') {
                  sectionContent = `<div style="text-align: center; font-weight: bold; font-size: 11px;">${row.section || ''}</div>`;
                }
                
                const rowClass = row.type === 'section-total' ? 'style="background-color: #f9fafb; font-weight: bold;"' : '';
                const headingClass = row.type === 'heading' ? 'class="heading-row"' : '';
                const subSectionClass = row.type === 'sub-section' ? 'class="sub-section-row"' : '';
                const breakClass = index % 20 === 19 ? 'class="break-after-page"' : '';
                const combinedClass = row.type === 'heading' && index % 20 === 19 ? 'class="heading-row break-after-page"' 
                  : row.type === 'sub-section' && index % 20 === 19 ? 'class="sub-section-row break-after-page"'
                  : (row.type === 'heading' ? headingClass : (row.type === 'sub-section' ? subSectionClass : breakClass));
                
                // Generate critical column content for custom modules with multiple rows
                let criticalContent = '';
                if (row.type === 'section-total' || row.type === 'heading' || row.type === 'sub-section') {
                  criticalContent = '';
                } else if (row.type === 'custom' && row.moduleInfo && row.criticalRows) {
                  // For custom modules, show checkboxes for each row
                  const criticalChecks = row.criticalRows.map(isCritical => isCritical ? '✓' : '').join('<br>');
                  criticalContent = criticalChecks;
                } else {
                  criticalContent = row.critical ? '✓' : '';
                }
                
                return `
                  <tr ${combinedClass} ${rowClass}>
                    <td class="section-col">${row.type === 'heading' ? '' : (row.type === 'sub-section' ? sectionContent : (row.section || ''))}</td>
                    <td class="task-col">${taskContent}</td>
                    <td class="critical-col text-center">${criticalContent}</td>
                    <td class="points-col text-center">${(row.type === 'heading' || row.type === 'sub-section') ? '' : pointsValue}</td>
                    <td class="score-col text-center"></td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div class="signature-section">
            <div class="signature-row">
              <div class="signature-field">
                <label>Examiner Signature:</label>
                <div class="signature-line"></div>
              </div>
              <div class="signature-field">
                <label>Date:</label>
                <div class="signature-line"></div>
              </div>
            </div>
            
            <div class="comments-section">
              <label style="font-weight: bold; font-size: 10px;">Comments:</label>
              <div class="comments-box"></div>
            </div>
            
            <div class="performance-section">
              <div class="performance-options">
                <span style="font-weight: bold;">Overall Performance:</span>
                <span>Pass <span class="checkbox"></span></span>
                <span>Fail <span class="checkbox"></span></span>
              </div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    // Open a new window with the print content
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // Add a new row at a specific index (or at the end if no index provided)
  const addRow = useCallback((atIndex = null) => {
    const newRow = {
      id: Date.now(),
      type: 'regular',
      section: '',
      task: '',
      critical: false,
      points: '' // Blank instead of 0
    };
    setAssessmentData(prev => {
      if (atIndex === null || atIndex >= prev.rows.length) {
        return {
          ...prev,
          rows: [...prev.rows, newRow]
        };
      }
      const newRows = [...prev.rows];
      newRows.splice(atIndex, 0, newRow);
      return {
        ...prev,
        rows: newRows
      };
    });
  }, []);

  // Add a section total row at a specific index
  const addSectionTotal = useCallback((atIndex = null) => {
    const newRow = {
      id: Date.now(),
      type: 'section-total',
      section: 'Section Total',
      task: '', // Blank task text
      critical: false,
      points: '', // This will be calculated automatically
      isCalculated: true
    };
    setAssessmentData(prev => {
      if (atIndex === null || atIndex >= prev.rows.length) {
        return {
          ...prev,
          rows: [...prev.rows, newRow]
        };
      }
      const newRows = [...prev.rows];
      newRows.splice(atIndex, 0, newRow);
      return {
        ...prev,
        rows: newRows
      };
    });
  }, []);

  // Add a heading row at a specific index
  const addHeadingRow = useCallback((atIndex = null) => {
    const newRow = {
      id: Date.now(),
      type: 'heading',
      section: '',
      task: '', // Only this field will be editable
      critical: false,
      points: ''
    };
    setAssessmentData(prev => {
      if (atIndex === null || atIndex >= prev.rows.length) {
        return {
          ...prev,
          rows: [...prev.rows, newRow]
        };
      }
      const newRows = [...prev.rows];
      newRows.splice(atIndex, 0, newRow);
      return {
        ...prev,
        rows: newRows
      };
    });
  }, []);

  // Add a sub section row at a specific index
  const addSubSection = useCallback((atIndex = null) => {
    const newRow = {
      id: Date.now(),
      type: 'sub-section',
      section: '', // Only this field will be editable
      task: '',
      critical: false,
      points: ''
    };
    setAssessmentData(prev => {
      if (atIndex === null || atIndex >= prev.rows.length) {
        return {
          ...prev,
          rows: [...prev.rows, newRow]
        };
      }
      const newRows = [...prev.rows];
      newRows.splice(atIndex, 0, newRow);
      return {
        ...prev,
        rows: newRows
      };
    });
  }, []);

  const rowsWithCalculatedTotals = useMemo(() => {
    return assessmentData.rows.map((row, index) => {
      if (row.type !== 'section-total') {
        return row;
      }

      // It's a section-total row, so calculate its value.
      let total = 0;
      let startIndex = 0;

      // Find the previous section total to determine where this section starts
      for (let i = index - 1; i >= 0; i--) {
        if (assessmentData.rows[i].type === 'section-total') {
          startIndex = i + 1;
          break;
        }
      }

      // Sum all points from startIndex to index (exclusive)
      for (let i = startIndex; i < index; i++) {
        const currentRow = assessmentData.rows[i];
        if (currentRow.type === 'regular' || currentRow.type === 'custom') {
          if (currentRow.type === 'custom' && currentRow.moduleInfo) {
            total += Number(currentRow.moduleInfo.totalPoints || currentRow.points || 0);
          } else {
            total += Number(currentRow.points || 0);
          }
        }
      }
      
      return { ...row, calculatedPoints: total };
    });
  }, [assessmentData.rows]);

  const addCustomModule = useCallback((moduleData) => {
    const newRow = {
      id: Date.now(),
      type: 'custom',
      section: moduleData.section || 'Assessment',
      task: moduleData.moduleName || 'Custom Module',
      critical: false,
      criticalRows: Array(moduleData.gridRows).fill(false), // Array of critical values, one per grid row
      points: moduleData.totalPoints || 1,
      moduleInfo: {
        moduleName: moduleData.moduleName,
        gridRows: moduleData.gridRows,
        gridColumns: moduleData.gridColumns,
        gridCells: moduleData.gridCells,
        totalPoints: moduleData.totalPoints
      }
    };

    setAssessmentData(prev => ({
      ...prev,
      rows: [...prev.rows, newRow]
    }));
  }, []);

  // Add custom module as rows
  const handleCustomModuleSave = useCallback((moduleData) => {
    if (editingCustomModule !== null) {
      // Update existing custom module
      setAssessmentData(prev => ({
        ...prev,
        rows: prev.rows.map(row => 
          row.id === editingCustomModule ? {
            ...row,
            section: moduleData.section || 'Assessment',
            task: moduleData.moduleName || 'Custom Module',
            points: moduleData.totalPoints || 1,
            criticalRows: Array(moduleData.gridRows).fill(false), // Reset critical rows when editing
            moduleInfo: {
              moduleName: moduleData.moduleName,
              gridRows: moduleData.gridRows,
              gridColumns: moduleData.gridColumns,
              gridCells: moduleData.gridCells,
              totalPoints: moduleData.totalPoints
            }
          } : row
        )
      }));
      setEditingCustomModule(null);
    } else {
      // Create a new custom module row
      const moduleRow = {
        id: Date.now(),
        type: 'custom',
        section: moduleData.section || 'Assessment',
        task: moduleData.moduleName || 'Custom Module',
        critical: false,
        criticalRows: Array(moduleData.gridRows).fill(false), // Array of critical values, one per grid row
        points: moduleData.totalPoints || 1,
        moduleInfo: {
          moduleName: moduleData.moduleName,
          gridRows: moduleData.gridRows,
          gridColumns: moduleData.gridColumns,
          gridCells: moduleData.gridCells,
          totalPoints: moduleData.totalPoints
        }
      };

      setAssessmentData(prev => ({
        ...prev,
        rows: [...prev.rows, moduleRow]
      }));
    }

    setShowCustomModuleBuilder(false);
  }, [editingCustomModule]);

  // Edit existing custom module
  const editCustomModule = useCallback((rowId) => {
    const row = assessmentData.rows.find(r => r.id === rowId);
    if (row && row.type === 'custom' && row.moduleInfo) {
      setEditingCustomModule(rowId);
      setShowCustomModuleBuilder(true);
    }
  }, [assessmentData.rows]);

  // Delete a row
  const deleteRow = useCallback((id) => {
    setAssessmentData(prev => ({
      ...prev,
      rows: prev.rows.filter(row => row.id !== id)
    }));
  }, []);

  // Handle drag and drop reordering and insertion
  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    // Check if it's a button being dragged (not a row)
    if (source.droppableId === 'button-palette') {
      // User is dragging a button to insert a new row
      const buttonType = draggableId.replace('button-', '');
      const insertIndex = destination.index;

      switch (buttonType) {
        case 'regular':
          addRow(insertIndex);
          break;
        case 'section-total':
          addSectionTotal(insertIndex);
          break;
        case 'heading':
          addHeadingRow(insertIndex);
          break;
        case 'sub-section':
          addSubSection(insertIndex);
          break;
        default:
          break;
      }
      return;
    }

    // Otherwise, it's a row being reordered
    if (source.droppableId === destination.droppableId) {
      const newRows = Array.from(assessmentData.rows);
      const [reorderedItem] = newRows.splice(source.index, 1);
      newRows.splice(destination.index, 0, reorderedItem);

      setAssessmentData(prev => ({
        ...prev,
        rows: newRows
      }));
    }
  }, [assessmentData.rows, addRow, addSectionTotal, addHeadingRow, addSubSection]);

  // Update header info
  const updateHeader = useCallback((field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Save current form to localStorage
  const saveForm = useCallback(() => {
    if (!saveFormName.trim()) {
      alert('Please enter a name for your form');
      return;
    }

    if (currentFormId) {
      // Update existing form
      const updatedForms = savedForms.map(form => 
        form.id === currentFormId 
          ? { ...form, name: saveFormName.trim(), savedAt: new Date().toISOString(), data: assessmentData }
          : form
      );
      setSavedForms(updatedForms);
      localStorage.setItem('nremt-saved-forms', JSON.stringify(updatedForms));
      setCurrentFormName(saveFormName.trim());
      alert('Form updated successfully!');
    } else {
      // Create new form
      const formToSave = {
        id: Date.now(),
        name: saveFormName.trim(),
        savedAt: new Date().toISOString(),
        data: assessmentData
      };

      const updatedForms = [...savedForms, formToSave];
      setSavedForms(updatedForms);
      localStorage.setItem('nremt-saved-forms', JSON.stringify(updatedForms));
      setCurrentFormId(formToSave.id);
      setCurrentFormName(formToSave.name);
      alert('Form saved successfully!');
    }
    
    setSaveFormName('');
    setShowSaveDialog(false);
  }, [assessmentData, savedForms, saveFormName, currentFormId]);

  // Load a saved form
  const loadForm = useCallback((savedForm) => {
    if (window.confirm(`Load "${savedForm.name}"? This will replace your current form.`)) {
      setAssessmentData(savedForm.data);
      setCurrentFormId(savedForm.id);
      setCurrentFormName(savedForm.name);
      setShowLoadDialog(false);
    }
  }, []);

  // Delete a saved form
  const deleteSavedForm = useCallback((formId) => {
    if (window.confirm('Are you sure you want to delete this saved form?')) {
      const updatedForms = savedForms.filter(form => form.id !== formId);
      setSavedForms(updatedForms);
      localStorage.setItem('nremt-saved-forms', JSON.stringify(updatedForms));
    }
  }, [savedForms]);

  // Create new blank form
  const createNewForm = useCallback(() => {
    if (window.confirm('Create a new form? This will clear your current work.')) {
      setAssessmentData({
        title: 'NREMT Practical Skill Assessment',
        candidateName: '',
        examinerName: '',
        date: new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        rows: []
      });
      setCurrentFormId(null);
      setCurrentFormName('');
      // Clear the current form from localStorage
      localStorage.removeItem('nremt-current-form');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white shadow-sm border-b no-print">
        <div className="max-w-7xl mx-auto px-2 py-2">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-bold text-gray-900">NREMT Print Builder</h1>
              {currentFormName && (
                <p className="text-xs text-gray-600 mt-0.5">Current Form: {currentFormName}</p>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={createNewForm}
                className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-xs font-medium"
              >
                New Form
              </button>
              {currentFormId && (
                <button
                  onClick={() => {
                    setSaveFormName(currentFormName);
                    setShowSaveDialog(true);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-medium"
                  title="Save changes to current form"
                >
                  💾 Save
                </button>
              )}
              <button
                onClick={() => {
                  setSaveFormName(currentFormName || '');
                  setShowSaveDialog(true);
                }}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-xs font-medium"
              >
                {currentFormId ? 'Save As...' : 'Save Form'}
              </button>
              <button
                onClick={() => setShowLoadDialog(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-medium"
              >
                Load Form
              </button>
              <button
                onClick={() => setShowPrintPreview(!showPrintPreview)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
              >
                {showPrintPreview ? 'Edit Mode' : 'Print Preview'}
              </button>
              <button
                onClick={generatePDF}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center space-x-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Export PDF</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-2 bg-white">
        
        {/* Assessment Header */}
        <div className="mb-3">
          <h1 className="text-lg font-bold text-center mb-3">{assessmentData.title}</h1>
          
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Candidate Name:</label>
              {showPrintPreview ? (
                <div className="border-b border-gray-400 h-5 text-xs">{assessmentData.candidateName}</div>
              ) : (
                <input
                  type="text"
                  value={assessmentData.candidateName}
                  onChange={(e) => updateHeader('candidateName', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                  placeholder="Enter candidate name"
                />
              )}
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Examiner Name:</label>
              {showPrintPreview ? (
                <div className="border-b border-gray-400 h-5 text-xs">{assessmentData.examinerName}</div>
              ) : (
                <input
                  type="text"
                  value={assessmentData.examinerName}
                  onChange={(e) => updateHeader('examinerName', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                  placeholder="Enter examiner name"
                />
              )}
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date:</label>
              {showPrintPreview ? (
                <div className="border-b border-gray-400 h-5 text-xs">{assessmentData.date}</div>
              ) : (
                <input
                  type="date"
                  value={assessmentData.date}
                  onChange={(e) => updateHeader('date', e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                />
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-1">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Start:</label>
                {showPrintPreview ? (
                  <div className="border-b border-gray-400 h-5 text-xs">{assessmentData.startTime}</div>
                ) : (
                  <input
                    type="time"
                    value={assessmentData.startTime}
                    onChange={(e) => updateHeader('startTime', e.target.value)}
                    className="w-full border border-gray-300 rounded px-1 py-1 text-xs"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">End:</label>
                {showPrintPreview ? (
                  <div className="border-b border-gray-400 h-5 text-xs">{assessmentData.endTime}</div>
                ) : (
                  <input
                    type="time"
                    value={assessmentData.endTime}
                    onChange={(e) => updateHeader('endTime', e.target.value)}
                    className="w-full border border-gray-300 rounded px-1 py-1 text-xs"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Assessment Table */}
        <div className="mb-3">
          {/* CLS Fix: Reserve space for the info text to prevent layout shift */}
          <div className="h-5 mb-1 text-xs text-gray-600 no-print">
            {!showPrintPreview && (
              <>
                Total rows: {assessmentData.rows.length} | 
                Estimated pages: {Math.ceil(assessmentData.rows.length / 18)} |
                <span className="text-blue-600 ml-1">Page breaks every ~18 rows</span>
              </>
            )}
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="overflow-x-auto relative table-container">
              <table className="max-w-6xl mx-auto p-2 bg-white w-full border-collapse border border-gray-400 text-xs break-inside-avoid">
              <thead>
                <tr className="bg-gray-100">
                  {!showPrintPreview && (
                    <th className="border border-gray-400 px-1 py-0 text-center font-semibold align-middle" style={{ width: '30px' }}>
                      <span className="text-gray-600">⠿</span>
                    </th>
                  )}
                  <th className="border border-gray-400 px-1 py-0 text-left font-semibold w-1/6 align-middle">Section</th>
                  <th className="border border-gray-400 px-1 py-0 text-left font-semibold align-middle" style={{ width: 'calc(100% - 160px)' }}>Task/Criteria</th>
                  <th className="border border-gray-400 px-1 py-0 text-center font-semibold align-middle" style={{ width: '60px' }}>Critical</th>
                  <th className="border border-gray-400 px-1 py-0 text-center font-semibold align-middle" style={{ width: '50px' }}>Points</th>
                  <th className="border border-gray-400 px-1 py-0 text-center font-semibold align-middle" style={{ width: '50px' }}>Score</th>
                </tr>
              </thead>
                <Droppable droppableId="assessment-rows">
                  {(provided) => (
                    <tbody {...provided.droppableProps} ref={provided.innerRef}>
                      {rowsWithCalculatedTotals.map((row, index) => (
                        <AssessmentRow
                          key={row.id}
                          row={row}
                          index={index}
                          showPrintPreview={showPrintPreview}
                          editingField={editingField}
                          tempValue={tempValue}
                          setTempValue={setTempValue}
                          startEditing={startEditing}
                          confirmEdit={confirmEdit}
                          cancelEdit={cancelEdit}
                          updateRow={updateRow}
                          updateCustomRowCritical={updateCustomRowCritical}
                          editCustomModule={editCustomModule}
                          deleteRow={deleteRow}
                        />
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
              
              {/* Actions Sidebar - positioned to align with table rows */}
              {!showPrintPreview && (
                <div className="absolute opacity-0 transition-opacity duration-200 bg-white border border-gray-400 z-10 actions-sidebar no-print" 
                     style={{ 
                       width: '30px', 
                       right: '0',
                       top: '0'
                     }}>
                  {/* Actions Header - matching exact table header styling */}
                  <div className="bg-gray-100 border-b border-gray-400 px-1 py-0 text-center font-semibold text-xs align-middle flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  
                  {/* Action buttons for each row */}
                  {assessmentData.rows.map((row, index) => {
                    // Calculate height for custom modules to match the actual table cell height
                    const rowHeight = row.type === 'custom' && row.moduleInfo 
                      ? `${row.moduleInfo.gridRows * 20 + 1}px` // 20px per grid row + 1px for border
                      : '24px'; // Default height for regular rows (h-6 = 24px)
                    
                    return (
                      <div 
                        key={row.id} 
                        className="border-b border-gray-400 px-1 py-0 text-center align-middle flex items-center justify-center hover:bg-red-50"
                        style={{ height: rowHeight }}
                      >
                        <button
                          onClick={() => deleteRow(row.id)}
                          className="text-red-600 hover:text-red-800 text-xs px-1 py-0 rounded transition-colors"
                          title="Delete row"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add Row Buttons */}
            {!showPrintPreview && (
              <div className="mb-3 no-print">
                <p className="text-xs text-gray-600 mb-2">Drag these buttons into the table to insert rows at specific positions:</p>
                <Droppable droppableId="button-palette" isDropDisabled={true}>
              {(provided) => (
                <div 
                  ref={provided.innerRef} 
                  {...provided.droppableProps}
                  className="flex flex-wrap gap-2"
                >
                  <Draggable draggableId="button-regular" index={0}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs font-medium cursor-grab active:cursor-grabbing ${
                          snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''
                        }`}
                      >
                        + Add Row
                      </div>
                    )}
                  </Draggable>
                  
                  <Draggable draggableId="button-section-total" index={1}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded text-xs font-medium cursor-grab active:cursor-grabbing ${
                          snapshot.isDragging ? 'shadow-lg ring-2 ring-indigo-400' : ''
                        }`}
                      >
                        + Section Total
                      </div>
                    )}
                  </Draggable>
                  
                  <Draggable draggableId="button-heading" index={2}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`bg-teal-600 hover:bg-teal-700 text-white px-2 py-1 rounded text-xs font-medium cursor-grab active:cursor-grabbing ${
                          snapshot.isDragging ? 'shadow-lg ring-2 ring-teal-400' : ''
                        }`}
                      >
                        + Heading Row
                      </div>
                    )}
                  </Draggable>
                  
                  <Draggable draggableId="button-sub-section" index={3}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`bg-cyan-600 hover:bg-cyan-700 text-white px-2 py-1 rounded text-xs font-medium cursor-grab active:cursor-grabbing ${
                          snapshot.isDragging ? 'shadow-lg ring-2 ring-cyan-400' : ''
                        }`}
                      >
                        + Sub Section
                      </div>
                    )}
                  </Draggable>
                  
                  <button
                    onClick={() => setShowCustomModuleBuilder(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs font-medium flex items-center space-x-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span>Custom Module</span>
                  </button>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        )}
        </DragDropContext>
      </div>

      {/* Footer/Signature Section */}
        <div className="mt-4 space-y-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Examiner Signature:</label>
              <div className="border-b border-gray-400 h-6"></div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date:</label>
              <div className="border-b border-gray-400 h-6"></div>
            </div>
          </div>
          
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Comments:</label>
            <div className="border border-gray-400 h-16 p-1"></div>
          </div>
          
          <div className="mt-3 text-center">
            <div className="inline-flex space-x-6">
              <label className="flex items-center">
                <span className="mr-2 font-medium text-xs">Overall Performance:</span>
                <span className="mr-1 text-xs">Pass</span>
                <div className="border border-gray-400 w-3 h-3"></div>
                <span className="mx-3 text-xs">Fail</span>
                <div className="border border-gray-400 w-3 h-3"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Module Builder */}
      <PrintNREMTCustomModuleBuilder
        isOpen={showCustomModuleBuilder}
        onClose={() => {
          setShowCustomModuleBuilder(false);
          setEditingCustomModule(null);
        }}
        onSave={handleCustomModuleSave}
        existingData={editingCustomModule ? assessmentData.rows.find(r => r.id === editingCustomModule)?.moduleInfo : null}
      />

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-bold mb-4">
              {currentFormId ? 'Save or Save As New Form' : 'Save Assessment Form'}
            </h2>
            {currentFormId && (
              <p className="text-sm text-gray-600 mb-3">
                Currently editing: <span className="font-semibold">{currentFormName}</span>
              </p>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Form Name:
              </label>
              <input
                type="text"
                value={saveFormName}
                onChange={(e) => setSaveFormName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveForm()}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Enter a name for your form"
                autoFocus
              />
            </div>
            <div className="flex space-x-3">
              {currentFormId ? (
                <>
                  <button
                    onClick={saveForm}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
                  >
                    Update Existing
                  </button>
                  <button
                    onClick={() => {
                      setCurrentFormId(null);
                      saveForm();
                    }}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded text-sm font-medium"
                  >
                    Save As New
                  </button>
                </>
              ) : (
                <button
                  onClick={saveForm}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded text-sm font-medium"
                >
                  Save
                </button>
              )}
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  setSaveFormName('');
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Dialog */}
      {showLoadDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-hidden">
            <h2 className="text-lg font-bold mb-4">Load Saved Form</h2>
            <div className="overflow-y-auto max-h-64 mb-4">
              {savedForms.length === 0 ? (
                <p className="text-gray-500 text-sm">No saved forms found.</p>
              ) : (
                <div className="space-y-2">
                  {savedForms.map((form) => (
                    <div key={form.id} className="flex items-center justify-between p-3 border border-gray-200 rounded hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{form.name}</div>
                        <div className="text-xs text-gray-500">
                          Saved: {new Date(form.savedAt).toLocaleDateString()} at {new Date(form.savedAt).toLocaleTimeString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {form.data.rows.length} rows
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => loadForm(form)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-medium"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => deleteSavedForm(form.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowLoadDialog(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AssessmentRow = React.memo(({
  row,
  index,
  showPrintPreview,
  editingField,
  tempValue,
  setTempValue,
  startEditing,
  confirmEdit,
  cancelEdit,
  updateRow,
  updateCustomRowCritical,
  editCustomModule,
  deleteRow,
}) => {
  // Calculate section total for section-total rows
  const displayPoints = useMemo(() => row.type === 'section-total' 
    ? row.calculatedPoints
    : (row.type === 'custom' && row.moduleInfo ? (row.moduleInfo.totalPoints || row.points) : row.points),
    [row]
  );

  return (
    <Draggable key={row.id} draggableId={String(row.id)} index={index} isDragDisabled={showPrintPreview}>
      {(provided, snapshot) => (
        <>
          {/* Drop indicator above row when dragging */}
          {snapshot.draggingOver && (
            <tr>
              <td colSpan={showPrintPreview ? 5 : 6} className="p-0">
                <div className="h-1 bg-blue-400 rounded-full mx-2"></div>
              </td>
            </tr>
          )}
          
          <tr 
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`${index % 20 === 19 ? 'break-after-page' : ''} ${row.type === 'section-total' ? 'bg-gray-50 font-semibold' : ''} page-break-inside-avoid hover:bg-blue-50 transition-colors duration-200 ease-in-out ${snapshot.isDragging ? 'shadow-lg bg-blue-100 ring-2 ring-blue-400' : ''}`}
          >
            {!showPrintPreview && (
              <td 
                {...provided.dragHandleProps}
                className="border border-gray-400 px-1 py-0 text-center align-middle h-6 cursor-grab active:cursor-grabbing"
              >
                <span className="text-gray-500">⠿</span>
              </td>
            )}
          <td className={`border border-gray-400 px-1 py-0 align-middle relative h-6 ${row.type === 'heading' || row.type === 'sub-section' ? 'border-r-0' : ''}`}>
            {!showPrintPreview && index % 20 === 19 && (
              <div className="absolute -right-6 top-0 text-xs text-blue-500 font-medium transform rotate-90 origin-left">
                Break
              </div>
            )}
            {row.type === 'sub-section' ? (
              showPrintPreview ? (
                <div className="text-center text-sm font-bold text-gray-800">
                  {row.section || ''}
                </div>
              ) : editingField === `section-${row.id}` ? (
                <div className="flex items-center justify-center space-x-1 w-full">
                  <input
                    type="text"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmEdit(row.id, 'section');
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    className="flex-1 border border-blue-300 bg-white text-sm font-bold text-center px-2 py-0 h-5 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
                    autoFocus
                    placeholder="Enter sub section text..."
                  />
                  <button
                    onClick={() => confirmEdit(row.id, 'section')}
                    className="text-green-600 hover:text-green-800 text-xs px-1"
                    title="Confirm"
                  >
                    ✓
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="text-red-600 hover:text-red-800 text-xs px-1"
                    title="Cancel"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => startEditing(`section-${row.id}`, row.section)}
                  className="w-full text-center text-sm font-bold p-0 h-full cursor-text hover:bg-blue-50 flex items-center justify-center px-2 rounded"
                  title="Click to edit sub section"
                >
                  {row.section || <span className="text-gray-400 font-normal">Click to add sub section...</span>}
                </div>
              )
            ) : row.type === 'heading' ? (
              <span className="text-xs"></span>
            ) : row.type === 'section-total' ? (
              <span className="text-xs font-semibold">{row.section}</span>
            ) : showPrintPreview ? (
              <span className="text-xs">{row.section}</span>
            ) : editingField === `section-${row.id}` ? (
              <div className="flex items-center space-x-1 w-full">
                <input
                  type="text"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmEdit(row.id, 'section');
                    if (e.key === 'Escape') cancelEdit();
                  }}
                  className="flex-1 border border-blue-300 bg-white text-xs px-1 py-0 h-4 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
                  autoFocus
                />
                <button
                  onClick={() => confirmEdit(row.id, 'section')}
                  className="text-green-600 hover:text-green-800 text-xs px-1"
                  title="Confirm"
                >
                  ✓
                </button>
                <button
                  onClick={cancelEdit}
                  className="text-red-600 hover:text-red-800 text-xs px-1"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div
                onClick={() => startEditing(`section-${row.id}`, row.section)}
                className="w-full text-xs p-0 h-full cursor-text hover:bg-blue-50 flex items-center px-1 rounded"
                title="Click to edit"
              >
                {row.section || <span className="text-gray-400">Section</span>}
              </div>
            )}
          </td>
          <td className={`border border-gray-400 ${row.type === 'custom' ? 'px-0' : 'px-1'} py-0 align-middle h-6 ${row.type === 'heading' || row.type === 'sub-section' ? 'border-l-0 border-r-0' : ''}`}>
            {row.type === 'sub-section' ? (
              <span className="text-xs"></span>
            ) : row.type === 'heading' ? (
              showPrintPreview ? (
                <div className="text-center text-sm font-bold text-gray-800">
                  {row.task || ''}
                </div>
              ) : editingField === `task-${row.id}` ? (
                <div className="flex items-center justify-center space-x-1 w-full">
                  <input
                    type="text"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmEdit(row.id, 'task');
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    className="flex-1 border border-blue-300 bg-white text-sm font-bold text-center px-2 py-0 h-5 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
                    autoFocus
                    placeholder="Enter heading text..."
                  />
                  <button
                    onClick={() => confirmEdit(row.id, 'task')}
                    className="text-green-600 hover:text-green-800 text-xs px-1"
                    title="Confirm"
                  >
                    ✓
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="text-red-600 hover:text-red-800 text-xs px-1"
                    title="Cancel"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => startEditing(`task-${row.id}`, row.task)}
                  className="w-full text-center text-sm font-bold p-0 h-full cursor-text hover:bg-blue-50 flex items-center justify-center px-2 rounded"
                  title="Click to edit heading"
                >
                  {row.task || <span className="text-gray-400 font-normal">Click to add heading...</span>}
                </div>
              )
            ) : row.type === 'section-total' ? (
              <span className="text-xs"></span>
            ) : row.type === 'custom' && row.moduleInfo ? (
              // Render custom module grid - Microsoft Word table style with internal borders only
              <div className="relative group">
                {!showPrintPreview && (
                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs cursor-pointer z-10"
                        title="Click to edit">
                    ✎
                  </div>
                )}
                <div 
                  className="w-full cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => !showPrintPreview && editCustomModule(row.id)}
                  title={!showPrintPreview ? "Click to edit custom module" : ""}
                  style={{ 
                    display: 'grid',
                    gridTemplateColumns: `repeat(${row.moduleInfo.gridColumns}, 1fr)`,
                    gridTemplateRows: `repeat(${row.moduleInfo.gridRows}, 1fr)`,
                    minHeight: `${row.moduleInfo.gridRows * 20}px`, // 20px per row to match regular row height
                    gap: '0'
                  }}
                >
                  {row.moduleInfo.gridCells.map((cell, cellIndex) => {
                    const gridRow = Math.floor(cellIndex / row.moduleInfo.gridColumns);
                    const gridCol = cellIndex % row.moduleInfo.gridColumns;
                    const isLastRow = gridRow === row.moduleInfo.gridRows - 1;
                    const isLastCol = gridCol === row.moduleInfo.gridColumns - 1;
                    
                    return (
                      <div 
                        key={cellIndex}
                        className="bg-white text-gray-700 flex items-center justify-center"
                        style={{ 
                          fontSize: '8px', 
                          minHeight: '20px',
                          borderRight: isLastCol ? 'none' : '1px solid #9CA3AF',
                          borderBottom: isLastRow ? 'none' : '1px solid #9CA3AF',
                          padding: '0',
                          margin: '0',
                          lineHeight: '1.1'
                        }}
                      >
                        {(() => {
                          const cellTypes = cell.types || [cell.type || 'blank'];
                          const hasText = cellTypes.includes('text');
                          const hasCheckbox = cellTypes.includes('checkbox');
                          
                          if (hasText && hasCheckbox) {
                            return (
                              <div className="flex items-center justify-center px-1 text-xs" style={{ width: '100%' }}>
                                <span style={{ marginRight: '2px' }}>
                                  {cell.content || ''}
                                </span>
                                <span>☐</span>
                              </div>
                            );
                          } else if (hasText) {
                            return (
                              <div className="flex items-center justify-center text-xs" style={{ width: '100%' }}>
                                <span>
                                  {cell.content || ''}
                                </span>
                              </div>
                            );
                          } else if (hasCheckbox) {
                            return (
                              <div className="flex items-center justify-center text-xs" style={{ width: '100%' }}>
                                <span>☐</span>
                              </div>
                            );
                          } else {
                            return '';
                          }
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : showPrintPreview ? (
              <span className="text-xs leading-tight">{row.task}</span>
            ) : editingField === `task-${row.id}` ? (
              <div className="flex items-center space-x-1 w-full">
                <input
                  type="text"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmEdit(row.id, 'task');
                    if (e.key === 'Escape') cancelEdit();
                  }}
                  className="flex-1 border border-blue-300 bg-white text-xs px-1 py-0 h-4 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
                  autoFocus
                />
                <button
                  onClick={() => confirmEdit(row.id, 'task')}
                  className="text-green-600 hover:text-green-800 text-xs px-1"
                  title="Confirm"
                >
                  ✓
                </button>
                <button
                  onClick={cancelEdit}
                  className="text-red-600 hover:text-red-800 text-xs px-1"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div
                onClick={() => startEditing(`task-${row.id}`, row.task)}
                className="w-full text-xs p-0 h-full cursor-text hover:bg-blue-50 flex items-center px-1 rounded"
                title="Click to edit"
              >
                {row.task || <span className="text-gray-400">Task description</span>}
              </div>
            )}
          </td>
          <td className={`border border-gray-400 ${row.type === 'custom' ? 'px-0' : 'px-1'} py-0 text-center align-middle h-6 ${row.type === 'heading' || row.type === 'sub-section' ? 'border-l-0 border-r-0' : ''}`}>
            {row.type === 'sub-section' ? (
              <span className="text-xs"></span>
            ) : row.type === 'heading' ? (
              <span className="text-xs"></span>
            ) : row.type === 'section-total' ? (
              // No checkbox for section totals
              <span className="text-xs"></span>
            ) : row.type === 'custom' && row.moduleInfo ? (
              // For custom modules, show one checkbox per grid row
              <div className="flex flex-col items-center justify-center" style={{ minHeight: `${row.moduleInfo.gridRows * 20}px` }}>
                {Array.from({ length: row.moduleInfo.gridRows }).map((_, rowIndex) => (
                  <div 
                    key={rowIndex} 
                    className="flex items-center justify-center" 
                    style={{ 
                      height: '20px',
                      borderBottom: rowIndex < row.moduleInfo.gridRows - 1 ? '1px solid #9CA3AF' : 'none',
                      width: '100%'
                    }}
                  >
                    {showPrintPreview ? (
                      <span className="text-sm">{row.criticalRows?.[rowIndex] ? '✓' : ''}</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={row.criticalRows?.[rowIndex] || false}
                        onChange={(e) => updateCustomRowCritical(row.id, rowIndex, e.target.checked)}
                        className="text-center scale-75"
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : showPrintPreview ? (
              <span className="text-sm">{row.critical ? '✓' : ''}</span>
            ) : (
              <input
                type="checkbox"
                checked={row.critical}
                onChange={(e) => updateRow(row.id, 'critical', e.target.checked)}
                className="text-center scale-75"
              />
            )}
          </td>
          <td className={`border border-gray-400 px-1 py-0 text-center align-middle h-6 ${row.type === 'heading' || row.type === 'sub-section' ? 'border-l-0' : ''}`}>
            {row.type === 'sub-section' ? (
              <span className="text-xs"></span>
            ) : row.type === 'heading' ? (
              <span className="text-xs"></span>
            ) : row.type === 'section-total' ? (
              // Section total - show calculated total, not editable
              <span className="text-xs font-bold">
                {displayPoints}
              </span>
            ) : row.type === 'custom' && row.moduleInfo ? (
              // Show total points for custom modules
              <span className="text-xs font-medium">
                {row.moduleInfo.totalPoints || row.points}
              </span>
            ) : showPrintPreview ? (
              <span className="text-xs text-gray-500"> </span>
            ) : (
              <input
                type="number"
                min="0"
                max="10"
                value={row.points}
                onChange={(e) => updateRow(row.id, 'points', e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                className="w-8 text-center border border-gray-300 rounded text-xs p-0 h-4 mx-auto no-arrows focus:outline-none focus:ring-1 focus:ring-blue-300"
              />
            )}
          </td>
          <td className="border border-gray-400 px-1 py-0 text-center align-middle h-6">
            {showPrintPreview ? (
              <span className="text-xs text-gray-500"> </span>
            ) : (
              <span className="text-xs text-gray-500"> </span>
            )}
          </td>
        </tr>
        </>
      )}
    </Draggable>
  );
});

export default NREMTPrintBuilderNew;
